import { ApolloClient } from '@apollo/client';
import { CUSTOMER_LIST_ITEM_QUERY } from './customers.gql.tsx';

export interface CustomerListRecord {
	customerId: string;
	displayName?: string | null;
	isCompany: boolean;
	firmName?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	marketingOptIn: boolean;
	fiscalNumber?: string | null;
	isActive: boolean;
	customerCategory?: {
		description?: string | null;
	} | null;
}

interface CustomerListItemResponse {
	customer?: CustomerListRecord | null;
}

export interface SelectCustomerAfterSaveOptions {
	customerId: string;
	client: ApolloClient<any>;
	currentCustomers: CustomerListRecord[];
	onDataUpdate: (updatedCustomers: CustomerListRecord[]) => void;
	onCustomerSelect: (customer: CustomerListRecord) => void;
}

/**
 * Fetches the latest customer data from the server and updates the list,
 * then selects and highlights the customer in the grid and details panel.
 * 
 * @param options Configuration options for selecting the customer
 * @returns Promise that resolves when the operation is complete
 */
export async function selectCustomerAfterSave(
	options: SelectCustomerAfterSaveOptions
): Promise<void> {
	const { customerId, client, currentCustomers, onDataUpdate, onCustomerSelect } = options;

	try {
		// Step 1: Fetch the latest customer data from the server
		const { data } = await client.query<CustomerListItemResponse>({
			query: CUSTOMER_LIST_ITEM_QUERY,
			variables: { id: customerId },
			fetchPolicy: 'network-only' // Always fetch fresh data from server
		});

		const fetchedCustomer = data?.customer;

		if (!fetchedCustomer) {
			console.warn(`Customer with ID ${customerId} not found after save`);
			return;
		}

		// Step 2: Check if customer exists in current list
		const existingCustomerIndex = currentCustomers.findIndex(
			c => c.customerId === customerId
		);

		let updatedCustomers: CustomerListRecord[];

		if (existingCustomerIndex >= 0) {
			// Customer exists - replace with updated data
			updatedCustomers = [...currentCustomers];
			updatedCustomers[existingCustomerIndex] = fetchedCustomer;
		} else {
			// New customer - append to the list
			updatedCustomers = [...currentCustomers, fetchedCustomer];
		}

		// Step 3: Update the list data
		onDataUpdate(updatedCustomers);

		// Step 4: Select the customer (both in grid and details panel)
		// Use setTimeout to ensure the grid has been updated before selection
		setTimeout(() => {
			onCustomerSelect(fetchedCustomer);
		}, 50);
	} catch (error) {
		console.error('Error selecting customer after save:', error);
		throw error;
	}
}
